import React, { useState, useEffect, useRef, useCallback } from 'react';
import { emailAPI, emailTemplatesAPI, customerAPI, taskAPI, documentAPI } from '../../services/api';
import { useAuth } from '../../AuthContext';
import { TEMPLATE_VARIABLE_GROUPS } from '../../constants/templateVariables';
import './emailComposer.css';

const EmailComposer = ({ project, onEmailSent }) => {
  const { isAdmin } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', subject: '', htmlContent: '' });
  const templateSubjectRef = useRef(null);
  const templateHtmlRef = useRef(null);
  // Tracks which template-form field was last focused so variable-chip clicks
  // know whether to insert into Subject or HTML Content.
  const [templateLastFocused, setTemplateLastFocused] = useState('html');

  const insertTemplateVariable = (varName) => {
    const token = `{{${varName}}}`;
    const fieldKey = templateLastFocused === 'subject' ? 'subject' : 'htmlContent';
    const ref = templateLastFocused === 'subject' ? templateSubjectRef : templateHtmlRef;
    const el = ref.current;
    const current = templateForm[fieldKey] || '';
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    const next = current.slice(0, start) + token + current.slice(end);
    setTemplateForm(prev => ({ ...prev, [fieldKey]: next }));
    setTimeout(() => {
      if (!el) return;
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    }, 0);
  };
  const [templateError, setTemplateError] = useState('');
  // From-address selection. '' means use the user's default connected Gmail.
  const [sendAsOptions, setSendAsOptions] = useState([]);
  const [fromAddress, setFromAddress] = useState('');
  // Tasks for this project — used to populate appointmentDate/Time/taskTitle
  // when the sender attaches a specific appointment to the email.
  const [tasks, setTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState('');
  // Project documents available to attach, plus the set the user has selected.
  const [documents, setDocuments] = useState([]);
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  const editorRef = useRef(null);
  // Contact autocomplete state for the To field.
  const [contactSuggestions, setContactSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const suggestTimerRef = useRef(null);

  const fetchContactSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setContactSuggestions([]);
      return;
    }
    try {
      const res = await customerAPI.getAll({ search: query, limit: 8 });
      // Only contacts with an email are useful as recipients.
      const withEmail = (res.data || []).filter(c => c.email);
      setContactSuggestions(withEmail);
    } catch (err) {
      console.error('Contact lookup failed:', err);
      setContactSuggestions([]);
    }
  }, []);

  const handleToChange = (e) => {
    const value = e.target.value;
    setToEmail(value);
    setShowSuggestions(true);
    setHighlightedIdx(-1);
    // Debounce so we don't fire on every keystroke.
    if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
    suggestTimerRef.current = setTimeout(() => fetchContactSuggestions(value), 200);
  };

  const pickContact = (contact) => {
    setToEmail(contact.email);
    setShowSuggestions(false);
    setContactSuggestions([]);
  };

  const contactDisplayLabel = (c) => {
    const name = c.displayName
      || [c.firstName, c.lastName].filter(Boolean).join(' ').trim()
      || c.companyName
      || '(unnamed)';
    return c.companyName && c.companyName !== name
      ? `${name} — ${c.companyName}`
      : name;
  };

  const handleToKeyDown = (e) => {
    if (!showSuggestions || contactSuggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx(i => Math.min(i + 1, contactSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightedIdx >= 0) {
      e.preventDefault();
      pickContact(contactSuggestions[highlightedIdx]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const syncEditorContent = useCallback(() => {
    if (editorRef.current) {
      setHtmlContent(editorRef.current.innerHTML);
    }
  }, []);

  const execFormat = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    syncEditorContent();
  };

  const handleInsertLink = () => {
    const url = prompt('Enter URL:');
    if (url) execFormat('createLink', url);
  };

  const handleTextColor = (e) => {
    execFormat('foreColor', e.target.value);
  };

  useEffect(() => {
    loadTemplates();
    loadSendAsOptions();
    if (project?.id) {
      loadTasks(project.id);
      loadDocuments(project.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);


  const loadTemplates = async () => {
    try {
      const res = await emailTemplatesAPI.getAll();
      setTemplates(res.data);
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const loadSendAsOptions = async () => {
    try {
      const res = await emailAPI.getSendAsOptions();
      setSendAsOptions(res.data);
    } catch (err) {
      console.error('Error loading send-as options:', err);
    }
  };

  const loadTasks = async (projectId) => {
    try {
      const res = await taskAPI.getByProject(projectId);
      // Sort: items with a DueDate first (soonest first), then no-date items.
      const sorted = [...(res.data || [])].sort((a, b) => {
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate) - new Date(b.dueDate);
      });
      setTasks(sorted);
    } catch (err) {
      console.error('Error loading tasks:', err);
    }
  };

  const loadDocuments = async (projectId) => {
    try {
      const res = await documentAPI.getByProject(projectId);
      setDocuments(res.data || []);
    } catch (err) {
      console.error('Error loading documents:', err);
    }
  };

  const toggleDoc = (docId) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const formatDocSize = (bytes) => {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const docIcon = (contentType, fileName) => {
    const ct = (contentType || '').toLowerCase();
    const name = (fileName || '').toLowerCase();
    if (ct.startsWith('image/')) return 'fa-file-image';
    if (ct === 'application/pdf' || name.endsWith('.pdf')) return 'fa-file-pdf';
    if (ct.includes('word') || /\.docx?$/.test(name)) return 'fa-file-word';
    if (ct.includes('sheet') || ct.includes('excel') || /\.xlsx?$|\.csv$/.test(name)) return 'fa-file-excel';
    if (ct.includes('zip') || /\.(zip|rar|7z)$/.test(name)) return 'fa-file-archive';
    return 'fa-file';
  };

  const buildVariables = () => {
    if (!project) return {};
    const customer = project.customer;
    const salesRep = project.salesRep;
    const statusLabels = [
      'Lead', 'Write Estimate', 'Pending Signature', 'Signed Contract',
      'Pending Start Date', 'Jobs In Progress', 'Job Completed',
      'Pending Payments', 'Cancelled', 'Lost Follow Up', 'Paid / Closed',
      'Send Warranty'
    ];
    const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '';
    const formatTime = (d) =>
      d ? new Date(d).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
    const selectedTask = tasks.find(t => String(t.id) === String(selectedTaskId));

    // Sales-rep display name: prefer first+last from Settings, fall back to username.
    const salesRepName = salesRep
      ? ([salesRep.firstName, salesRep.lastName].filter(Boolean).join(' ').trim() || salesRep.username || '')
      : '';

    // jobIdentifier: PO# if set, else numeric project ID (matches backend).
    const jobIdentifier = project.poNumber && project.poNumber.trim()
      ? project.poNumber
      : (project.id?.toString() || '');

    return {
      // ---- Customer ----
      customerName: customer ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim() : '',
      customerFirstName: customer?.firstName || '',
      customerLastName: customer?.lastName || '',
      customerDisplayName: customer?.displayName || '',
      customerEmail: customer?.email || '',
      customerPhone: customer?.phone || '',
      customerMobilePhone: customer?.mobilePhone || '',
      companyName: customer?.companyName || '',

      // ---- Tenant (PM jobs) ----
      tenantName: project.tenantName || '',
      tenantContact: project.tenantContact || '',

      // ---- Address ----
      jobAddress: customer ? `${customer.propertyStreet || ''}, ${customer.propertyCity || ''}, ${customer.propertyState || ''} ${customer.propertyZipCode || ''}`.trim() : '',
      propertyStreet: customer?.propertyStreet || '',
      addressLine2: customer?.addressLine2 || '',
      propertyCity: customer?.propertyCity || '',
      propertyState: customer?.propertyState || '',
      propertyZip: customer?.propertyZipCode || '',
      gateCode: customer?.gateCode || '',

      // ---- Project ----
      projectName: project.projectName || '',
      projectStatus: statusLabels[project.status] || 'Unknown',
      jobId: project.id?.toString() || '',
      poNumber: project.poNumber || '',
      jobIdentifier,
      leadSource: project.leadSource || '',
      currentInstaller: project.currentInstaller || '',

      // ---- Insurance / Claims ----
      insuranceCarrier: project.insuranceCarrier || '',
      claimNumber: project.claimNumber || '',

      // ---- Work Details ----
      shingleType: project.shingleType || '',
      shingleColor: project.shingleColor || '',
      sidingColor: project.sidingColor || '',
      gutterGuards: project.gutterGuards ? 'Yes' : 'No',
      gutterColor: project.gutterColor || '',
      gutterFeet: project.gutterFeet != null ? String(project.gutterFeet) : '',
      downspoutFeet: project.downspoutFeet != null ? String(project.downspoutFeet) : '',
      oneStoryDownspouts: project.oneStoryDownspoutCount != null ? String(project.oneStoryDownspoutCount) : '',
      twoStoryDownspouts: project.twoStoryDownspoutCount != null ? String(project.twoStoryDownspoutCount) : '',

      // ---- Work Order / Notes ----
      workOrderType: project.workOrderType || '',
      workOrderText: project.workOrderText || '',
      repairDescription: project.repairDescription || '',
      repairCompletionNotes: project.repairCompletionNotes || '',
      notRoofRelatedNotes: project.notRoofRelatedNotes || '',
      overviewReportNotes: project.overviewReportNotes || '',

      // ---- Dates ----
      date: new Date().toLocaleDateString(),
      completionDate: formatDate(project.completionDate),
      scheduledStartDate: formatDate(project.scheduledStartDate),
      workOrderStartDate: formatDate(project.workOrderStartDate),
      materialDeliveryDate: formatDate(project.materialDeliveryDate),

      // ---- Sales Rep ----
      assignedUser: salesRep?.username || 'Unassigned',
      salesRepName,
      salesRepPhone: salesRep?.phone || '',

      // ---- Appointment (task-scoped, optional) ----
      appointmentDate: formatDate(selectedTask?.dueDate),
      appointmentTime: formatTime(selectedTask?.dueDate),
      taskTitle: selectedTask?.title || '',
    };
  };

  const replaceVars = (text) => {
    const vars = buildVariables();
    let result = text;
    Object.entries(vars).forEach(([key, value]) => {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'gi'), value);
    });
    // Strip any remaining {{word}} tokens that weren't in the dictionary so the
    // preview matches what the backend will actually send (the backend does the
    // same final strip in ReplaceVariables).
    return result.replace(/\{\{[A-Za-z0-9_]+\}\}/g, '');
  };

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplateId(templateId);
    if (!templateId) {
      setSubject('');
      setHtmlContent('');
      if (editorRef.current) editorRef.current.innerHTML = '';
    }
    // Actual substitution happens in the effect below so picking a task
    // afterward also triggers a re-render.
  };

  // Re-process the template whenever the chosen template OR task changes.
  // Trades off losing manual body edits when the user picks a task afterward;
  // pick-task-then-edit is the intended order.
  useEffect(() => {
    if (!selectedTemplateId) return;
    const template = templates.find(t => t.id === parseInt(selectedTemplateId));
    if (!template) return;
    const processed = replaceVars(template.htmlContent);
    setSubject(replaceVars(template.subject));
    setHtmlContent(processed);
    if (editorRef.current) editorRef.current.innerHTML = processed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateId, selectedTaskId, templates]);

  const handleSend = async () => {
    if (!toEmail || !subject) {
      setMessage({ text: 'Please fill in To and Subject fields.', type: 'error' });
      return;
    }

    setSending(true);
    setMessage({ text: '', type: '' });

    try {
      const fromPayload = fromAddress ? { fromAddress } : {};
      const taskPayload = selectedTaskId ? { taskId: parseInt(selectedTaskId) } : {};
      const docPayload = selectedDocIds.size > 0 ? { documentIds: Array.from(selectedDocIds) } : {};
      let res;
      if (selectedTemplateId) {
        // Pass the composer's current subject/body as overrides so any
        // last-minute edits the user made aren't ignored. Variables in
        // these still resolve server-side through BuildProjectVariables.
        res = await emailAPI.sendTemplate({
          templateId: parseInt(selectedTemplateId),
          projectId: project.id,
          toEmail,
          extraVariables: {},
          overrideSubject: subject,
          overrideHtmlContent: htmlContent,
          ...fromPayload,
          ...taskPayload,
          ...docPayload,
        });
      } else {
        res = await emailAPI.send({
          toEmail,
          subject,
          htmlContent: htmlContent || `<p>${subject}</p>`,
          projectId: project.id,
          ...fromPayload,
          ...docPayload,
        });
      }

      // The server tells us if it had to split the send into multiple parts.
      setMessage({ text: res?.data?.message || 'Email sent successfully!', type: 'success' });
      setSubject('');
      setHtmlContent('');
      setSelectedTemplateId('');
      setSelectedTaskId('');
      setFromAddress('');
      setSelectedDocIds(new Set());
      if (editorRef.current) editorRef.current.innerHTML = '';
      if (onEmailSent) onEmailSent();
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Failed to send email.';
      setMessage({ text: errMsg, type: 'error' });
    } finally {
      setSending(false);
    }
  };

  // Template modal handlers
  const openTemplateModal = () => {
    setTemplateForm({ name: '', subject: '', htmlContent: '' });
    setTemplateError('');
    setShowTemplateModal(true);
  };

  const handleTemplateFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setTemplateForm(prev => ({ ...prev, htmlContent: event.target.result }));
    };
    reader.readAsText(file);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.htmlContent) {
      setTemplateError('All fields are required.');
      return;
    }
    try {
      await emailTemplatesAPI.create(templateForm);
      setShowTemplateModal(false);
      loadTemplates();
    } catch (err) {
      setTemplateError(err.response?.data?.message || 'Failed to save template.');
    }
  };

  return (
    <div className="email-composer">
      <div className="email-composer-header">
        <h3>Compose Email</h3>
      </div>

      <div className="email-composer-form">
        <div className="email-form-row">
          <label>Template</label>
          <div className="template-select-row">
            <select value={selectedTemplateId} onChange={handleTemplateChange}>
              <option value="">-- No Template (Custom) --</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            {isAdmin() && (
              <button className="btn-add-template" onClick={openTemplateModal} title="Add Template">
                <i className="fas fa-plus"></i>
              </button>
            )}
          </div>
        </div>

        {/* Task picker — only relevant for template sends since substitution
            happens server-side on that path. Skip the row entirely when no
            template is selected or there are no tasks on the project. */}
        {selectedTemplateId && tasks.length > 0 && (
          <div className="email-form-row">
            <label>Appointment</label>
            <select
              value={selectedTaskId}
              onChange={(e) => setSelectedTaskId(e.target.value)}
              title="Attach a task so {{appointmentDate}}, {{appointmentTime}}, and {{taskTitle}} resolve."
            >
              <option value="">-- None --</option>
              {tasks.map(t => {
                const date = t.dueDate ? new Date(t.dueDate).toLocaleString([], {
                  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                }) : 'no date';
                return (
                  <option key={t.id} value={t.id}>
                    {t.title} — {date}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        {sendAsOptions.length > 0 && (
          <div className="email-form-row">
            <label>From</label>
            <select value={fromAddress} onChange={(e) => setFromAddress(e.target.value)}>
              <option value="">My email (default)</option>
              {sendAsOptions.map(opt => (
                <option key={opt.address} value={opt.address}>
                  {opt.label} ({opt.address})
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="email-form-row">
          <label>To</label>
          <div className="email-to-autocomplete">
            <input
              type="email"
              value={toEmail}
              onChange={handleToChange}
              onKeyDown={handleToKeyDown}
              onFocus={() => toEmail.length >= 2 && setShowSuggestions(true)}
              // Delay so a click on a suggestion lands before we hide the list.
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              placeholder="recipient@example.com — or start typing a contact name"
              autoComplete="off"
            />
            {showSuggestions && contactSuggestions.length > 0 && (
              <ul className="contact-suggestions">
                {contactSuggestions.map((c, idx) => (
                  <li
                    key={c.id}
                    className={`contact-suggestion ${idx === highlightedIdx ? 'highlighted' : ''}`}
                    onMouseDown={(e) => { e.preventDefault(); pickContact(c); }}
                    onMouseEnter={() => setHighlightedIdx(idx)}
                  >
                    <div className="contact-suggestion-name">{contactDisplayLabel(c)}</div>
                    <div className="contact-suggestion-email">{c.email}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="email-form-row">
          <label>Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject"
          />
        </div>

        <div className="email-form-row">
          <label>Body</label>
          <div className="email-editor-wrapper">
            <div className="email-toolbar">
              <button type="button" className="toolbar-btn" onClick={() => execFormat('bold')} title="Bold">
                <i className="fas fa-bold"></i>
              </button>
              <button type="button" className="toolbar-btn" onClick={() => execFormat('italic')} title="Italic">
                <i className="fas fa-italic"></i>
              </button>
              <button type="button" className="toolbar-btn" onClick={() => execFormat('underline')} title="Underline">
                <i className="fas fa-underline"></i>
              </button>
              <span className="toolbar-divider" />
              <button type="button" className="toolbar-btn" onClick={() => execFormat('formatBlock', '<h3>')} title="Heading">
                <i className="fas fa-heading"></i>
              </button>
              <button type="button" className="toolbar-btn" onClick={() => execFormat('insertUnorderedList')} title="Bullet List">
                <i className="fas fa-list-ul"></i>
              </button>
              <button type="button" className="toolbar-btn" onClick={() => execFormat('insertOrderedList')} title="Numbered List">
                <i className="fas fa-list-ol"></i>
              </button>
              <span className="toolbar-divider" />
              <button type="button" className="toolbar-btn" onClick={() => execFormat('justifyLeft')} title="Align Left">
                <i className="fas fa-align-left"></i>
              </button>
              <button type="button" className="toolbar-btn" onClick={() => execFormat('justifyCenter')} title="Align Center">
                <i className="fas fa-align-center"></i>
              </button>
              <button type="button" className="toolbar-btn" onClick={() => execFormat('justifyRight')} title="Align Right">
                <i className="fas fa-align-right"></i>
              </button>
              <span className="toolbar-divider" />
              <button type="button" className="toolbar-btn" onClick={handleInsertLink} title="Insert Link">
                <i className="fas fa-link"></i>
              </button>
              <label className="toolbar-btn toolbar-color" title="Text Color">
                <i className="fas fa-palette"></i>
                <input type="color" onChange={handleTextColor} defaultValue="#000000" />
              </label>
              <button type="button" className="toolbar-btn" onClick={() => execFormat('removeFormat')} title="Clear Formatting">
                <i className="fas fa-eraser"></i>
              </button>
            </div>
            <div
              ref={editorRef}
              className="email-editor"
              contentEditable
              onInput={syncEditorContent}
              data-placeholder="Compose your email..."
            />
          </div>
        </div>

        {documents.length > 0 && (
          <div className="email-form-row">
            <label>Attach</label>
            <div className="email-attach-picker">
              {documents.map(doc => {
                const selected = selectedDocIds.has(doc.id);
                return (
                  <button
                    type="button"
                    key={doc.id}
                    className={`email-attach-option${selected ? ' selected' : ''}`}
                    onClick={() => toggleDoc(doc.id)}
                    title={doc.fileName}
                  >
                    <i className={`fas ${selected ? 'fa-check-square' : 'fa-square'} email-attach-check`}></i>
                    <i className={`fas ${docIcon(doc.contentType, doc.fileName)} email-attach-type`}></i>
                    <span className="email-attach-name">{doc.fileName}</span>
                    {doc.fileSize != null && (
                      <span className="email-attach-size">{formatDocSize(doc.fileSize)}</span>
                    )}
                  </button>
                );
              })}
            </div>
            {selectedDocIds.size > 0 && (() => {
              const totalBytes = documents
                .filter(d => selectedDocIds.has(d.id))
                .reduce((sum, d) => sum + (d.fileSize || 0), 0);
              // ~17MB raw budget per email (mirrors the backend packer).
              const willSplit = totalBytes > 17 * 1024 * 1024;
              return (
                <div className="email-attach-summary">
                  {formatDocSize(totalBytes)} selected
                  {willSplit && (
                    <span className="email-attach-split-note">
                      <i className="fas fa-info-circle"></i>
                      Over 25&nbsp;MB — will be sent as multiple emails (real attachments, no links).
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {message.text && (
          <div className={`email-message ${message.type}`}>
            <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
            {message.text}
          </div>
        )}

        <button
          className="btn-send-email"
          onClick={handleSend}
          disabled={sending}
        >
          {sending ? (
            <><i className="fas fa-spinner fa-spin"></i> Sending...</>
          ) : (
            <><i className="fas fa-paper-plane"></i> Send Email</>
          )}
        </button>
      </div>

      {/* Add Template Modal */}
      {showTemplateModal && (
        // Closing is the X and Cancel only — no click-to-close on the backdrop.
        <div className="modal-overlay">
          <div className="modal-content et-modal">
            <div className="modal-header">
              <h2>New Email Template</h2>
              <button className="modal-close" onClick={() => setShowTemplateModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {templateError && <div className="et-error">{templateError}</div>}

              <div className="form-group">
                <label>Template Name</label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={e => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Invoice Follow-Up"
                />
              </div>

              <div className="form-group">
                <label>Subject</label>
                <input
                  ref={templateSubjectRef}
                  type="text"
                  value={templateForm.subject}
                  onChange={e => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                  onFocus={() => setTemplateLastFocused('subject')}
                  placeholder="e.g. Invoice for {{projectName}}"
                />
              </div>

              <div className="form-group">
                <label>HTML Content</label>
                <div className="et-upload-row">
                  <label className="et-upload-btn">
                    <i className="fas fa-upload"></i> Upload HTML File
                    <input type="file" accept=".html,.htm" onChange={handleTemplateFileUpload} hidden />
                  </label>
                  <span className="et-or">or paste below</span>
                </div>
                <textarea
                  ref={templateHtmlRef}
                  value={templateForm.htmlContent}
                  onChange={e => setTemplateForm(prev => ({ ...prev, htmlContent: e.target.value }))}
                  onFocus={() => setTemplateLastFocused('html')}
                  placeholder="Paste HTML content here... Click a variable below to insert it at the cursor."
                  rows={12}
                />
              </div>

              <div className="et-variables-hint">
                <strong>Available variables</strong>
                <span className="et-vars-help"> — click to insert at cursor</span>
                {TEMPLATE_VARIABLE_GROUPS.map(group => (
                  <div key={group.category} className="et-vars-group">
                    <em>{group.category}:</em>
                    {group.vars.map(name => (
                      <button
                        key={name}
                        type="button"
                        className="et-var-chip"
                        onClick={() => insertTemplateVariable(name)}
                        title={`Insert {{${name}}}`}
                      >
                        {`{{${name}}}`}
                      </button>
                    ))}
                  </div>
                ))}
              </div>

              {templateForm.htmlContent && (
                <div className="et-modal-preview">
                  <label>Preview</label>
                  <div className="et-preview-content" dangerouslySetInnerHTML={{ __html: templateForm.htmlContent }} />
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowTemplateModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleSaveTemplate}>Create Template</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailComposer;
